import type { ReactNode } from 'react'

interface EmptyStateProps {
  icon: ReactNode
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
  }
  /** design §5.5: "Empty states use the one EmptyState component
   * everywhere" — including small inline spots (a panel's claim grid, a
   * detail card's sub-section) where the default full-panel layout (64px
   * icon circle, py-16) would visually dominate a compact area. `compact`
   * keeps the SAME component, copy contract and icon/title/description/
   * action shape — it only shrinks the spacing and icon size, never
   * changes what content is required or how a caller uses it. Default
   * (false) preserves every existing full-panel usage unchanged. */
  compact?: boolean
}

export default function EmptyState({ icon, title, description, action, compact = false }: EmptyStateProps) {
  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center py-4 px-3 text-center" data-testid="empty-state-compact">
        <div className="w-8 h-8 rounded-full bg-iflow-50 text-iflow-500 flex items-center justify-center mb-2 [&>svg]:w-4 [&>svg]:h-4">
          {icon}
        </div>
        <h3 className="text-xs font-semibold text-gray-700 mb-0.5">{title}</h3>
        <p className="text-[11px] text-gray-500 max-w-xs mb-2">{description}</p>
        {action && (
          <button
            onClick={action.onClick}
            className="px-2.5 py-1 bg-iflow-700 text-white text-[11px] font-medium rounded-md hover:bg-iflow-800 transition-colors"
          >
            {action.label}
          </button>
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-iflow-50 text-iflow-500 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 max-w-sm mb-6">{description}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
